import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, View } from 'react-native';

import styles from './dnd-activity.style';
import ImageFragment from './image-fragment/image-fragment';
import { TemplateType } from '../../../../enums/template-type';
import { useActivityContext } from '../../../../hooks/use-activity.context';
import useDevice from '../../../../hooks/use-device';
import { useErrorSound } from '../../../../hooks/use-error-sound';
import { useSuccessSound } from '../../../../hooks/use-success-sound';
import useVibration from '../../../../hooks/use-vibration';
import { shuffle } from '../../../../utils/shuffle';
import Fragment, { FragmentOptionType } from '../../../fragment/fragment';
import { HintButton, HintButtonType } from '../../../hint-button/hint-button';
import KeyboardLegend, {
  KeyboardLegendPadding,
} from '../../../keyboard-legend/keyboard-legend';
import LessonAccentBanner from '../../../lesson-accent-banner/lesson-accent-banner';
import LessonInstructions from '../../../lesson-instructions/lesson-instructions';
import LessonPlayerFrame from '../../../lesson-player-frame/lesson-player-frame';

export interface WordImageInterface {
  word: string;
  image: string;
}

export interface DNDActivityInterface {
  type: 'DND' | 'DND - EXTENDED';
  instructions: string;
  instructionsTranslation?: string;
  correctFeedback: string;
  incorrectFeedback: string;
  correctFeedbackTranslation?: string;
  incorrectFeedbackTranslation?: string;
  steps: WordImageInterface[];
}

const ASPECT_RATIO = 1.7;

export function DNDActivity() {
  const { t } = useTranslation();
  const { isTablet, isMobile } = useDevice();
  const activityContext = useActivityContext();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();
  const { vibrate } = useVibration();
  const { completeCurrentActivity, isCurrentActivityComplete } =
    activityContext;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState<FragmentOptionType[]>([]);
  const [options, setOptions] = useState<FragmentOptionType[]>([]);
  const [isComplete, setComplete] = useState(false);
  const [isErrorVisible, setErrorVisible] = useState(false);
  const currentActivity =
    activityContext.currentActivity as DNDActivityInterface;
  const wordImages = currentActivity.steps;
  const { width } = Dimensions.get('window');
  const [hintButtonType, setHintButtonType] = useState<HintButtonType>('hint');
  const [showHintButton, setShowHintButton] = useState(false);

  const fragmentWidth = useMemo(() => {
    if (isMobile || isTablet) {
      return width / 3;
    }
    return 240;
  }, [isMobile, isTablet, width]);

  const images = useMemo(() => {
    return wordImages.map(({ image }) => image);
  }, [wordImages]);

  const shuffledWords = useMemo(() => {
    return shuffle(wordImages).map(({ word }, key) => ({
      text: word,
      key,
    }));
  }, [wordImages]);

  const hasAnsweredAll = useMemo(() => {
    return userAnswer.length > 0 && userAnswer.every((answer) => !!answer);
  }, [userAnswer]);

  const handleImagePress = useCallback(
    (index: number) => {
      if (isComplete) {
        return;
      }

      const option = userAnswer?.[index];
      if (option) {
        setOptions(options.map((opt, i) => (i === option?.key ? option : opt)));
        setUserAnswer(
          userAnswer.map((answer, i) => (index === i ? null : answer))
        );
      }
      setSelectedIndex(index);
    },
    [userAnswer, options, isComplete]
  );

  const handleWordPress = useCallback(
    (option: FragmentOptionType) => {
      setUserAnswer(
        userAnswer.map((answer, index) =>
          index === selectedIndex ? option : answer
        )
      );
      setOptions(options.map((opt) => (opt?.key === option?.key ? null : opt)));
    },
    [userAnswer, options, selectedIndex]
  );

  const resetWrongAnswers = useCallback(() => {
    const newOptions = [...options];
    userAnswer.forEach((answer, index) => {
      if (answer && answer?.text !== wordImages[index].word) {
        newOptions[answer.key] = { ...answer, isWrong: true };
      }
    });
    setOptions(newOptions);
    setUserAnswer(
      userAnswer.map((answer, index) =>
        answer?.text === wordImages[index].word ? answer : null
      )
    );
  }, [userAnswer, wordImages, options]);

  useEffect(() => {
    setOptions(shuffledWords);
    setUserAnswer(wordImages.map(() => null));
  }, [shuffledWords, wordImages]);

  useEffect(() => {
    if (!hasAnsweredAll || isComplete) {
      return;
    }

    const isCorrect = userAnswer.every(
      (answer, index) => answer?.text === wordImages[index].word
    );

    if (isCorrect) {
      correctSound.play();
      completeCurrentActivity();
      setComplete(true);
    } else {
      incorrectSound.play();
      vibrate();
      resetWrongAnswers();
      setErrorVisible(true);
      setTimeout(() => {
        setShowHintButton(true);
        setErrorVisible(false);
      }, 2000);
    }
  }, [
    vibrate,
    hasAnsweredAll,
    completeCurrentActivity,
    correctSound,
    incorrectSound,
    resetWrongAnswers,
    userAnswer,
    wordImages,
  ]);

  useEffect(() => {
    if (
      !userAnswer?.[selectedIndex] &&
      selectedIndex < userAnswer.length &&
      selectedIndex >= 0
    ) {
      return;
    }
    const index = userAnswer.findIndex((answer) => !answer);
    setSelectedIndex(index);
  }, [userAnswer, selectedIndex]);

  const fillAnswers = (numOfItems: number) => {
    const hintAnswers: FragmentOptionType[] = [];
    const remainingOptions: FragmentOptionType[] = [...shuffledWords];

    wordImages.forEach((wordImage, index) => {
      if (index < numOfItems) {
        const index = remainingOptions.findIndex(
          (value) => value?.text === wordImage.word
        );

        hintAnswers.push(remainingOptions[index]);
        remainingOptions[index] = null;
      } else {
        hintAnswers.push(null);
      }
    });
    setUserAnswer(hintAnswers);
    setOptions(remainingOptions);
  };

  const handleHintPress = () => {
    if (hintButtonType === 'hint') {
      setHintButtonType('answer');
      fillAnswers(2);
    } else {
      fillAnswers(4);
    }
  };

  return (
    <>
      <LessonPlayerFrame showContinueButton={isComplete}>
        <LessonAccentBanner
          accentStyle={styles.accentBanner}
          innerContainerStyle={styles.innerContainerStyle}
        >
          <LessonInstructions
            translation={currentActivity.instructionsTranslation}
          >
            {currentActivity.instructions}
          </LessonInstructions>
          <View style={styles.imagesContainer}>
            {images.map((image, index) => (
              <ImageFragment
                key={image}
                image={image}
                isComplete={isComplete}
                onPress={() => handleImagePress(index)}
                isSelected={selectedIndex === index}
                option={userAnswer[index]}
                fragmentStyle={{ width: fragmentWidth }}
                imageStyle={{
                  width: fragmentWidth,
                  height: fragmentWidth / ASPECT_RATIO,
                }}
              />
            ))}
          </View>
        </LessonAccentBanner>
        {!isComplete && (
          <>
            <View style={styles.content}>
              {options.map((option, index) => (
                <Fragment
                  key={`${index}`}
                  type="option"
                  option={option}
                  onPress={handleWordPress}
                  isErrorVisible={isErrorVisible}
                  fragmentStyle={{ width: fragmentWidth }}
                  placeholderStyle={{ width: fragmentWidth }}
                />
              ))}
            </View>
            {showHintButton && (
              <HintButton
                type={hintButtonType}
                colorType="gray"
                onPress={handleHintPress}
                containerStyle={styles.hintButton}
              />
            )}
            <KeyboardLegendPadding />
          </>
        )}
      </LessonPlayerFrame>
      <KeyboardLegend
        templateType={TemplateType.DND}
        forceHide={
          userAnswer?.some((answer) => !!answer) || isCurrentActivityComplete
        }
        text={t('keyboardLegend.answer')}
        indicatorList={options.map((_, i) => (i + 1).toString())}
      />
    </>
  );
}
